/*
 * XOVIST microSD card country gate: IP fallback (v3, 2026-10-09)
 * ------------------------------------------------------------------
 * Why:     the store runs a single market, so a UK or EU visitor may never pick a country
 *          and localization.country stays the default (US). The Liquid gates
 *          (snippets/bundle-and-save.liquid, snippets/cart-drawer-upsell.liquid) then let the
 *          64GB microSD card through. This file covers that case in the browser.
 * List:    snippets/xv-no-card-countries.liquid, handed over by snippets/global-typography-fix.liquid
 *          as data-countries on this script tag (one list for Liquid and JS).
 * Country: GET /browsing_context_suggestions.json?country[enabled]=true ->
 *          detected_values.country.handle (ISO code), cached in sessionStorage 'xv_card_geo'.
 *          Skipped when the store country is already on the list (Liquid has done the job).
 * If the visitor's IP country is on the list:
 *   - product page: the card's Frequently Bought Together item is hidden and its checkbox
 *     unticked and disabled, so neither assets/xv-pdp.js nor the block's own button adds it.
 *     A block left with no item is hidden whole, and the muted note is shown in its place
 *     (same text and look as the Liquid gate);
 *   - cart drawer: the card's "Goes well with it" upsell is hidden, and the header too when
 *     no other upsell is left. Re-applied after every drawer re-render.
 * Add-to-cart guard (review 2026-10-10, v3.1): one capture listener on window, so it runs before
 *   assets/xv-pdp.js (document capture) and the block's own button. On a click on Add to cart
 *   (button[name=add], .sticky-add-to-cart__button, [data-bundle-add-btn]):
 *   - country on the list: the gate is re-applied synchronously first, so a card a re-render
 *     has just ticked again is unticked before anything reads the boxes;
 *   - lookup still running AND a card is ticked: that one click is held until the lookup ends
 *     (at most HOLD_MS, once per page), the gate is applied if the country is on the list, and
 *     the same button is clicked again. Nothing else is held; other countries get the card as before.
 * Never adds, removes or changes anything in the cart itself, and makes no other request.
 * If the lookup fails or returns no country, nothing changes (a held click is released at once).
 * Shared (v4, 2026-10-10): xvCardGate.country() is the detected country once settled ('' = unknown or
 *   no lookup), and xvCardGate.onSettle(fn) calls fn(country) once it is settled (at once if it already
 *   is). assets/xv-ship-geo.js uses them, so the store makes this one lookup only.
 */
(function () {
  'use strict';
  if (window.xvCardGate) return;

  var me = document.currentScript || document.getElementById('xv-card-gate-js');
  function attr(n) { return (me && me.getAttribute(n)) || ''; }
  function codes(s) {
    var out = [];
    String(s || '').toUpperCase().split(',').forEach(function (c) {
      c = c.replace(/\s+/g, '');
      if (/^[A-Z]{2}$/.test(c) && out.indexOf(c) < 0) out.push(c);
    });
    return out;
  }

  var COUNTRIES = codes(attr('data-countries'));
  var STORE_COUNTRY = attr('data-store-country').toUpperCase();
  var CARD_HANDLE = attr('data-card-handle') || '32gb-microsd-memory-card';
  /* the card product (Admin API, 2026-10-09). Only used where the markup carries no handle:
     the FBT item has data-product-id and a checkbox whose value is the variant id. */
  var CARD_PRODUCT_IDS = ['8616321089715'];
  var CARD_VARIANT_IDS = ['45867245928627'];
  var GEO_KEY = 'xv_card_geo';         /* sessionStorage: detected 2-letter country */
  var TRIES_KEY = 'xv_card_geo_tries'; /* sessionStorage: failed lookups this session */
  var MAX_TRIES = 2;
  var GEO_URL = '/browsing_context_suggestions.json?country[enabled]=true';
  var NOTE = "Note: the camera records to a microSD card (not included). We can't ship cards to your country, so please use your own.";
  var HIDDEN = 'data-xv-card-hidden';
  var ATC = 'button[name="add"], .sticky-add-to-cart__button, [data-bundle-add-btn]';
  var HOLD_MS = 1500;

  var state = 'pending';  /* 'pending' (lookup not finished) | 'blocked' | 'clear' */
  var blocked = false;
  var memGeo = '';
  var memTries = 0;
  var timer = 0;
  var held = null;        /* the Add to cart button whose click is waiting for the lookup */
  var holdTimer = 0;
  var holdUsed = false;   /* one hold per page view at most */
  var replaying = false;
  var geo = '';           /* the detected country once settled, '' if unknown (xvCardGate.country()) */
  var waiting = [];       /* xvCardGate.onSettle callbacks */

  /* ---------- storage (may throw or be empty) ---------- */
  function sget(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function sset(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* ignore */ } }

  function each(sel, fn, root) {
    var list = (root || document).querySelectorAll(sel);
    for (var i = 0; i < list.length; i++) fn(list[i]);
  }
  function hide(el) {  /* idempotent: also restores a hidden state a re-render took away */
    if (!el) return;
    if (el.getAttribute(HIDDEN) !== '1') el.setAttribute(HIDDEN, '1');
    if (!el.hidden) el.hidden = true;
    if (el.style.getPropertyValue('display') !== 'none' || el.style.getPropertyPriority('display') !== 'important') {
      el.style.setProperty('display', 'none', 'important');
    }
  }
  function shown(el) {
    return el.getAttribute(HIDDEN) !== '1' && !el.hidden && el.style.display !== 'none';
  }

  /* ---------- product page: Frequently Bought Together (snippets/xv-bundle-and-save-core.liquid) ---------- */
  function isCardItem(item) {
    var pid = item.getAttribute('data-product-id');
    if (pid && CARD_PRODUCT_IDS.indexOf(String(pid)) > -1) return true;
    var inp = item.querySelector('input[name^="bundle_product_"]');
    return !!(inp && CARD_VARIANT_IDS.indexOf(String(inp.value)) > -1);
  }
  function addNoteStyle() {
    if (document.getElementById('xv-card-note-style')) return;
    var st = document.createElement('style');
    st.id = 'xv-card-note-style';
    /* the same rule snippets/bundle-and-save.liquid prints with its note */
    st.textContent = 'html body p.xv-card-note:not(#_){margin:0 0 10px !important;padding:0 !important;' +
      'font-size:12.5px !important;line-height:1.45 !important;font-weight:400 !important;' +
      'color:rgba(17,17,17,.62) !important;letter-spacing:-.005em !important;text-align:left !important;}';
    (document.head || document.documentElement).appendChild(st);
  }
  function addNote(block) {
    var prev = block.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('xv-card-note')) return;
    addNoteStyle();
    var p = document.createElement('p');
    p.className = 'xv-card-note';
    p.setAttribute('data-xv-card-gate', '');
    p.textContent = NOTE;
    block.parentNode.insertBefore(p, block);
  }
  function gateBundles() {
    each('[id^="bundle-save-"][data-block-id]', function (block) {
      var cards = [];
      each('.bundle-product-item', function (item) { if (isCardItem(item)) cards.push(item); }, block);
      if (!cards.length) return;
      var fixed = false;
      cards.forEach(function (item) {
        each('input[name^="bundle_product_"]', function (inp) {
          if (inp.type === 'checkbox') {
            if (inp.checked) {
              inp.checked = false;
              /* not bubbling: only the block's own listener (its price summary) hears it */
              try { inp.dispatchEvent(new Event('change')); } catch (e) { /* old browser */ }
            }
            inp.disabled = true;
          } else {
            fixed = true; /* fixed-bundle mode: always added by the block's button */
            inp.disabled = true;
          }
        }, item);
        var sep = item.previousElementSibling;
        if (sep && sep.classList.contains('bundle-plus-separator-wrapper')) hide(sep);
        else {
          sep = item.nextElementSibling;
          if (sep && sep.classList.contains('bundle-plus-separator-wrapper')) hide(sep);
        }
        hide(item);
      });
      var left = 0;
      each('.bundle-product-item', function (item) { if (shown(item)) left++; }, block);
      if (!left || fixed) hide(block);
      if (block.parentNode) addNote(block);
    });
  }

  /* ---------- cart drawer: "Goes well with it" upsell (snippets/cart-drawer-upsell.liquid) ---------- */
  function isCardUpsell(c) {
    var h = c.getAttribute('data-xv-upsell-handle');
    if (h) return h === CARD_HANDLE;
    var id = c.querySelector('form input[name="id"]');
    if (id && CARD_VARIANT_IDS.indexOf(String(id.value)) > -1) return true;
    var js = c.querySelector('script[id^="ProductJson-"]');
    if (js) {
      try { return (JSON.parse(js.textContent) || {}).handle === CARD_HANDLE; } catch (e) { /* ignore */ }
    }
    return false;
  }
  function gateDrawer() {
    var scopes = [];
    each('.upsell-product-container', function (c) {
      if (!isCardUpsell(c)) return;
      hide(c);
      var s = c.closest('.scrollable-contents') || c.closest('cart-drawer-items') || c.parentNode;
      if (s && scopes.indexOf(s) < 0) scopes.push(s);
    });
    /* the header only goes when the card was the last upsell under it */
    scopes.forEach(function (s) {
      var left = 0;
      each('.upsell-product-container', function (c) { if (shown(c)) left++; }, s);
      if (!left) each('.upsell-header-wrapper', hide, s);
    });
  }

  function apply() {
    if (!blocked) return;
    gateBundles();
    gateDrawer();
  }
  /* throttled, not debounced: a page that keeps adding nodes can never postpone it */
  function later() {
    if (!blocked || timer) return;
    timer = setTimeout(function () { timer = 0; apply(); }, 30);
  }

  function watch() {
    /* drawer re-renders (assets/cart-drawer.js, assets/cart.js) */
    document.addEventListener('cart-drawer:render', later);
    document.addEventListener('cart-drawer:update', later);
    document.addEventListener('cart-drawer:open', later);
    window.addEventListener('pageshow', later);
    window.addEventListener('load', later);
    /* any other re-render (drawer markup swapped in, product info replaced): new elements only */
    if (window.MutationObserver && document.body) {
      new MutationObserver(function (recs) {
        for (var i = 0; i < recs.length; i++) {
          var a = recs[i].addedNodes;
          for (var j = 0; j < a.length; j++) {
            if (a[j].nodeType === 1) { later(); return; }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ---------- Add to cart guard ---------- */
  function cardTicked() {  /* a card FBT item that Add to cart would add right now */
    var hit = false;
    each('[id^="bundle-save-"][data-block-id] .bundle-product-item', function (item) {
      if (hit || !isCardItem(item)) return;
      each('input[name^="bundle_product_"]', function (inp) {
        if (!inp.disabled && (inp.type !== 'checkbox' || inp.checked)) hit = true;
      }, item);
    });
    return hit;
  }
  function release() {
    clearTimeout(holdTimer);
    holdTimer = 0;
    var btn = held;
    held = null;
    if (!btn) return;
    if (blocked) apply();
    /* re-rendered meanwhile (the shopper clicks again), or now inside a hidden card block:
       a fixed-bundle button adds its hidden inputs even when they are disabled, so never replay it */
    if (!document.documentElement.contains(btn) || btn.closest('[' + HIDDEN + ']')) return;
    replaying = true;
    try { btn.click(); } catch (e) { /* ignore */ }
    replaying = false;
  }
  function onClick(e) {
    if (replaying) return;
    var t = e.target;
    var btn = t && t.closest ? t.closest(ATC) : null;
    if (!btn) return;
    if (blocked) {                         /* untick before xv-pdp.js reads the boxes */
      apply();
      if (btn.closest('[' + HIDDEN + ']')) { e.preventDefault(); e.stopImmediatePropagation(); }
      return;
    }
    if (state !== 'pending') return;
    if (!held && (holdUsed || !cardTicked())) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (held) return;                      /* repeat clicks while waiting: one replay only */
    held = btn;
    holdUsed = true;
    holdTimer = setTimeout(release, HOLD_MS);
  }

  function settle(country) {
    if (state !== 'pending') return;
    geo = country || '';
    if (country && COUNTRIES.indexOf(country) > -1) {
      state = 'blocked';
      blocked = true;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { apply(); watch(); });
      } else {
        apply();
        watch();
      }
    } else {
      state = 'clear';
    }
    var fns = waiting;
    waiting = [];
    fns.forEach(call);
    release();
  }
  function call(fn) { try { fn(geo); } catch (e) { /* a listener never breaks the gate */ } }

  function detect() {
    var c = (sget(GEO_KEY) || memGeo || '').toUpperCase();
    if (/^[A-Z]{2}$/.test(c)) { settle(c); return; }
    var tries = Math.max(Number(sget(TRIES_KEY)) || 0, memTries);
    if (tries >= MAX_TRIES || !window.fetch) { settle(''); return; }
    function failed() { memTries = tries + 1; sset(TRIES_KEY, String(memTries)); settle(''); }
    fetch(GEO_URL, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var h = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
        h = String(h || '').toUpperCase();
        if (!/^[A-Z]{2}$/.test(h)) { failed(); return; }
        memGeo = h;
        sset(GEO_KEY, h);
        settle(h);
      })
      .catch(failed);
  }

  window.xvCardGate = {
    version: '2026-10-10-v4',
    countries: COUNTRIES.slice(),
    apply: apply,
    isBlocked: function () { return blocked; },
    state: function () { return state; },
    country: function () { return geo; },
    onSettle: function (fn) {
      if (typeof fn !== 'function') return;
      if (state === 'pending') waiting.push(fn); else call(fn);
    }
  };

  /* no list, or the store country is already on it (the Liquid gates did the work): nothing to do */
  if (!COUNTRIES.length || COUNTRIES.indexOf(STORE_COUNTRY) > -1) { state = 'clear'; return; }

  window.addEventListener('click', onClick, true);
  detect();
})();
